import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MobileNav from '../../components/feature/MobileNav';
import { supabase } from '../../lib/supabase';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  category: string;
  image: string;
  stock: number;
  rating: number;
  sizes?: string[];
  flavors?: string[];
  tax_rate?: number;
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [product, setProduct] = useState<Product | null>(null);
  
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedFlavor, setSelectedFlavor] = useState('');
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    const { data } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .single();
    
    if (data) setProduct(data);
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">{t('productDetail.productNotFound')}</p>
          <Link to="/products" className="text-teal-600 font-semibold">
            {t('productDetail.backToShop')}
          </Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    if ((product.category === 'shoes' || product.category === 'uniformer') && product.sizes && product.sizes.length > 0 && !selectedSize) {
      alert(t('productDetail.pleaseSelectSize'));
      return;
    }
    if (product.flavors && product.flavors.length > 0 && !selectedFlavor) {
      alert(t('productDetail.pleaseSelectFlavor'));
      return;
    }

    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItemIndex = cart.findIndex(
      (item: any) =>
        item.id === product.id &&
        item.size === selectedSize &&
        item.flavor === selectedFlavor
    );

    if (existingItemIndex > -1) {
      cart[existingItemIndex].quantity += quantity;
    } else {
      cart.push({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity,
        size: selectedSize,
        flavor: selectedFlavor,
        tax_rate: product.tax_rate || 0.10,
      });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 2000);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    setTimeout(() => navigate('/cart'), 500);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Notification */}
      {showNotification && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-teal-600 text-white px-6 py-3 rounded-full shadow-lg z-50 flex items-center gap-2">
          <i className="ri-check-line text-xl"></i>
          <span>{t('productDetail.addedToCart')}</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-gray-600">
            <i className="ri-arrow-left-line text-2xl"></i>
          </button>
          <h1 className="text-lg font-bold text-gray-900">{t('productDetail.title')}</h1>
          <Link to="/cart" className="text-gray-600">
            <i className="ri-shopping-cart-2-line text-2xl"></i>
          </Link>
        </div>
      </div>

      {/* Product Image */}
      <div className="bg-white">
        <div className="relative w-full h-96">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover object-center"
          />
        </div>
      </div>

      {/* Product Info */}
      <div className="px-6 py-6 bg-white mt-2">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h2>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center">
                <i className="ri-star-fill text-yellow-500 mr-1"></i>
                <span>{product.rating}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-box-3-line mr-1"></i>
                <span>{product.stock} {t('productDetail.inStock')}</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-teal-600">{product.price} kr</div>
          </div>
        </div>

        <p className="text-gray-700 leading-relaxed">{product.description}</p>
      </div>

      {/* Size Selection for Shoes */}
      {(product.category === 'shoes' || product.category === 'uniformer') && product.sizes && (
        <div className="px-6 py-6 bg-white mt-2">
          <h3 className="font-bold text-gray-900 mb-4">{t('productDetail.selectSize')}</h3>
          <div className="grid grid-cols-6 gap-3">
            {product.sizes.map(size => (
              <button
                key={size}
                onClick={() => setSelectedSize(size)}
                className={`py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  selectedSize === size
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Flavor Selection for Drinks */}
      {product.flavors && product.flavors.length > 0 && (
        <div className="px-6 py-6 bg-white mt-2">
          <h3 className="font-bold text-gray-900 mb-4">{t('Välj')}</h3>
          <div className="grid grid-cols-2 gap-3">
            {product.flavors.map(flavor => (
              <button
                key={flavor}
                onClick={() => setSelectedFlavor(flavor)}
                className={`py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  selectedFlavor === flavor
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {flavor}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quantity */}
      <div className="px-6 py-6 bg-white mt-2">
        <h3 className="font-bold text-gray-900 mb-4">Antal</h3>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <i className="ri-subtract-line text-xl"></i>
          </button>
          <span className="text-2xl font-bold text-gray-900 w-12 text-center">{quantity}</span>
          <button
            onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
            className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <i className="ri-add-line text-xl"></i>
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 z-40">
        <button
          onClick={handleAddToCart}
          className="w-full bg-gray-900 text-white py-4 rounded-full font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap"
        >
          {t('LÄGG I VARUKORG')}
        </button>
      </div>

      <MobileNav />
    </div>
  );
}